import { useSyncExternalStore } from "react";

/**
 * "Not now" for the HappyBridge upgrade offer — one answer, shared by every surface that asks.
 *
 * ONE key for the strip and the dismissible card: a clinic is answering the offer, not the pixel
 * they clicked, and a per-surface key clears one and leaves the other, which reads as a dismiss
 * button that does not work.
 *
 * Settings does NOT participate (`BridgeUpgradeCta` without `dismissible`), and that permanent
 * surface is load-bearing: this offer is the only in-app route to the wizard, so without it one tap
 * on × takes a clinic's only way to upgrade away for five days.
 *
 * Per BROWSER, not per clinic — a clinic-scoped dismissal would let whoever is at the front desk
 * hide the offer from the owner, who is the only person who can act on it.
 *
 * A store rather than component state because the surfaces must agree WITHIN a render: dismissing
 * the card unmounts it, dropping its `offerSurface` announcement, which would otherwise let the
 * strip render into the space just cleared.
 */
export const BRIDGE_UPGRADE_DISMISS_KEY = "bridge_upgrade_banner_dismissed";

/** Five days. Long enough not to nag, short enough that a busy week doesn't bury the offer. */
const DISMISS_DURATION = 5 * 24 * 60 * 60 * 1000;

/**
 * Fails OPEN in every direction, each written out rather than left to the arithmetic:
 * unreadable storage → show; a non-number → show (NaN comparisons are false, which was correct by
 * accident); a FUTURE timestamp → show. That last one WAS wrong: a tablet with a bad clock storing
 * 2031 made `Date.now() - future` negative, hiding the offer for years.
 */
function readDismissedFromStorage(): boolean {
    try {
        const raw = localStorage.getItem(BRIDGE_UPGRADE_DISMISS_KEY);
        if (!raw) return false;
        const dismissedAt = parseInt(raw, 10);
        if (!Number.isFinite(dismissedAt) || dismissedAt > Date.now()) return false;
        return Date.now() - dismissedAt < DISMISS_DURATION;
    } catch (err) {
        console.warn("Could not read bridge upgrade dismissal from localStorage", err);
        return false;
    }
}

/**
 * Set when a dismissal could not be written to storage, so the answer lives only in this tab.
 *
 * Without it a private-window dismissal did not survive one navigation: `refresh()` re-read storage
 * on the next surface mount, found nothing, and moved the snapshot back to `false`. Dies with the
 * tab, which is the honest consequence of storage being off.
 */
let dismissedThisSessionOnly = false;

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshot = typeof window === "undefined" ? false : readDismissedFromStorage();

function emit(): void {
    // `forEach`, not `for...of`: this repo's tsconfig target predates downlevel iteration.
    listeners.forEach((listener) => listener());
}

/**
 * Re-reads storage and notifies if the answer moved, on SUBSCRIBE — so the window expires without a
 * page reload, which matters on exam-room tablets that go days without one. Not on a timer: a
 * tablet parked on one screen sees the offer return only once something navigates. Accepted; the
 * five days are a "don't nag" heuristic, not a deadline.
 */
function refresh(): void {
    const next = dismissedThisSessionOnly || readDismissedFromStorage();
    if (next === snapshot) return;
    snapshot = next;
    emit();
}

function subscribe(listener: Listener): () => void {
    refresh();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): boolean {
    return snapshot;
}

/** Whether the offer has been dismissed and is still inside the window. */
export function useBridgeUpgradeDismissed(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Records "not now" and tells every mounted surface at once. The in-memory snapshot is set even
 * when the write throws, so a private-window clinic sees their click do something.
 */
export function dismissBridgeUpgrade(): void {
    try {
        localStorage.setItem(BRIDGE_UPGRADE_DISMISS_KEY, Date.now().toString());
    } catch (err) {
        console.warn("Could not persist bridge upgrade dismissal to localStorage", err);
        // Storage will not remember this; `refresh()` must not read the absence as "not dismissed"
        // and undo it on the next surface mount. See `dismissedThisSessionOnly`.
        dismissedThisSessionOnly = true;
    }
    snapshot = true;
    emit();
}

/** Test-only: drop subscribers and re-read storage between cases. */
export function resetBridgeUpgradeDismissalForTests(): void {
    listeners.clear();
    dismissedThisSessionOnly = false;
    snapshot = readDismissedFromStorage();
}
